
import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";
import { getCurrentCustomer } from "../../utils/auth";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../context/SettingsContext";

const Dashboard = () => {
  const { t } = useTranslation();
  const { currencySymbol } = useSettings();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const customer = getCurrentCustomer();

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchOrders = async () => {
      if (!customer?.id) {
        setLoading(false);
        return;
      }

      try {
        const res = await api.get("/customer/my-orders", {
          params: { customer_id: customer.id },
        });

        setOrders(res?.data?.data?.data || []);
      } catch (error) {
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [customer?.id]);

  const stats = useMemo(() => {
    const total = orders.length;
    const pending = orders.filter((o) => o.status === "pending").length;
    const processing = orders.filter((o) => o.status === "processing").length;
    const completed = orders.filter((o) => o.status === "delivered").length;

    return { total, pending, processing, completed };
  }, [orders]);

  const formatAmount = (amount) => {
    return `${currencySymbol} ${Number(amount || 0).toFixed(2)}`;
  };

  const translateStatus = (status) => {
    if (!status) return "-";

    return t(`status.${status?.toLowerCase()}`, {
      defaultValue: status,
    });
  };

  const getOrderStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "bg-orange-100 text-orange-700";
      case "processing":
        return "bg-indigo-100 text-indigo-700";
      case "delivered":
        return "bg-emerald-100 text-emerald-700";
      case "cancelled":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getPaymentStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return "bg-emerald-100 text-emerald-700";
      case "unpaid":
        return "bg-red-100 text-red-700";
      case "pending":
        return "bg-orange-100 text-orange-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const StatCard = ({ title, value, icon, iconClass }) => {
    return (
      <div className="flex h-full">
        <div className="flex items-center border border-gray-200 w-full rounded-lg p-3 sm:p-4 bg-white">
          <div
            className={`flex items-center justify-center rounded-full h-10 w-10 sm:h-12 sm:w-12 text-lg sm:text-xl text-center mr-3 sm:mr-4 ${iconClass}`}
          >
            {icon}
          </div>

          <div className="min-w-0">
            <h5 className="leading-tight mb-1 sm:mb-2 text-xs sm:text-base font-medium text-gray-700">
              {title}
            </h5>

            <p className="text-lg sm:text-xl font-bold leading-none text-gray-800">
              {value}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="overflow-hidden">
      <h2 className="text-xl text-black font-semibold mb-5">
        {t("dashboard.title", "Dashboard")}
      </h2>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-8 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title={t("dashboard.totalOrder", "Total Order")}
          value={stats.total}
          iconClass="text-red-600 bg-red-200"
          icon={
            <svg
              stroke="currentColor"
              fill="none"
              strokeWidth="2"
              viewBox="0 0 24 24"
              strokeLinecap="round"
              strokeLinejoin="round"
              height="1em"
              width="1em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
          }
        />

        <StatCard
          title={t("dashboard.pendingOrder", "Pending Order")}
          value={stats.pending}
          iconClass="text-orange-600 bg-orange-200"
          icon={
            <svg
              stroke="currentColor"
              fill="none"
              strokeWidth="2"
              viewBox="0 0 24 24"
              strokeLinecap="round"
              strokeLinejoin="round"
              height="1em"
              width="1em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <polyline points="23 4 23 10 17 10"></polyline>
              <polyline points="1 20 1 14 7 14"></polyline>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
          }
        />

        <StatCard
          title={t("dashboard.processingOrder", "Processing Order")}
          value={stats.processing}
          iconClass="text-indigo-600 bg-indigo-200"
          icon={
            <svg
              stroke="currentColor"
              fill="none"
              strokeWidth="2"
              viewBox="0 0 24 24"
              strokeLinecap="round"
              strokeLinejoin="round"
              height="1em"
              width="1em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="1" y="3" width="15" height="13"></rect>
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
              <circle cx="5.5" cy="18.5" r="2.5"></circle>
              <circle cx="18.5" cy="18.5" r="2.5"></circle>
            </svg>
          }
        />

        <StatCard
          title={t("dashboard.completeOrder", "Complete Order")}
          value={stats.completed}
          iconClass="text-emerald-600 bg-emerald-200"
          icon={
            <svg
              stroke="currentColor"
              fill="none"
              strokeWidth="2"
              viewBox="0 0 24 24"
              strokeLinecap="round"
              strokeLinejoin="round"
              height="1em"
              width="1em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          }
        />
      </div>

      {/* Recent Orders */}
      <div className="bg-white border border-gray-100 rounded-md overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 font-medium text-gray-700">
          {t("dashboard.recentOrders", "Recent Orders")}
        </div>

        {loading ? (
          <div className="p-4 text-gray-500">
            {t("dashboard.loading", "Loading...")}
          </div>
        ) : orders.length === 0 ? (
          <div className="p-4 text-gray-500">
            {t("dashboard.noOrders", "No recent orders found.")}
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden divide-y divide-gray-100">
              {orders.slice(0, 5).map((order) => (
                <div key={order.id} className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">
                        {t("dashboard.tableOrder", "Order")}
                      </p>

                      <h4 className="text-sm font-semibold text-gray-800 break-all">
                        #{order.order_number || order.id}
                      </h4>
                    </div>

                    <div className="text-right shrink-0">
                      <p className="text-xs text-gray-500 mb-1">
                        {t("dashboard.tableTotal", "Total")}
                      </p>

                      <p className="text-sm font-bold text-gray-900">
                        {formatAmount(order.total_amount)}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-gray-500">
                        {t("dashboard.tableStatus", "Status")}
                      </span>

                      <span
                        className={`capitalize px-2 py-1 rounded-full text-xs font-medium ${getOrderStatusClass(
                          order.status
                        )}`}
                      >
                        {translateStatus(order.status)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <span className="text-gray-500">
                        {t("dashboard.tablePayment", "Payment")}
                      </span>

                      <span
                        className={`capitalize px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusClass(
                          order.payment_status
                        )}`}
                      >
                        {translateStatus(order.payment_status)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="table-auto min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left text-xs font-semibold px-6 py-2 text-gray-700 uppercase">
                      {t("dashboard.tableOrder", "Order")}
                    </th>

                    <th className="text-left text-xs font-semibold px-6 py-2 text-gray-700 uppercase">
                      {t("dashboard.tableStatus", "Status")}
                    </th>

                    <th className="text-left text-xs font-semibold px-6 py-2 text-gray-700 uppercase">
                      {t("dashboard.tablePayment", "Payment")}
                    </th>

                    <th className="text-right text-xs font-semibold px-6 py-2 text-gray-700 uppercase">
                      {t("dashboard.tableTotal", "Total")}
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.slice(0, 5).map((order) => (
                    <tr key={order.id}>
                      <td className="px-6 py-3 text-sm text-gray-700">
                        #{order.order_number || order.id}
                      </td>

                      <td className="px-6 py-3 text-sm text-gray-700">
                        <span
                          className={`capitalize px-2 py-1 rounded-full text-xs font-medium ${getOrderStatusClass(
                            order.status
                          )}`}
                        >
                          {translateStatus(order.status)}
                        </span>
                      </td>

                      <td className="px-6 py-3 text-sm text-gray-700">
                        <span
                          className={`capitalize px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusClass(
                            order.payment_status
                          )}`}
                        >
                          {translateStatus(order.payment_status)}
                        </span>
                      </td>

                      <td className="px-6 py-3 text-sm text-right font-medium text-gray-800">
                        {formatAmount(order.total_amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;